import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { AnnouncementView } from '@workflow/contracts'
import { ActionMenu, Avatar, AvatarStack, Button, COLLAPSE, Dialog, EmptyNote, IconButton, Icons, Loading, cn } from '@workflow/ui'
import { navigate } from '@/app/router'
import {
  useAllAnnouncements,
  useAnnouncements,
  useArchiveAnnouncement,
  useClosePoll,
  useCreateAnnouncement,
  useMarkAnnouncementSeen,
  usePinAnnouncement,
  useRestoreAnnouncement,
  useUnpinAnnouncement,
  useSession,
  useUpdateAnnouncement,
  useVote,
} from '@/lib/api/hooks'
import { formatDay, formatRelative } from '@/lib/format'
import { PageFrame } from '@/shell/PageFrame'
import { IMAGE_LINE, Markdown } from './Markdown'
import { Tool, WritingSurface } from '@/features/writing/WritingSurface'

const seenBy = (item: AnnouncementView): string =>
  item.viewers.length === 0 ? 'Not seen yet' : `Seen by ${item.viewers.length}`

/** The readers' faces with their count — the studio's confirmation it landed. */
function Viewers({ item, className }: { item: AnnouncementView; className?: string }) {
  return (
    <span className={cn('flex items-center gap-2', className)}>
      {item.viewers.length > 0 ? (
        <AvatarStack
          people={item.viewers.map((viewer) => ({ key: viewer.id, name: viewer.name, email: viewer.email, src: viewer.pictureUrl }))}
          max={3}
         
        />
      ) : null}
      <span className="whitespace-nowrap text-caption text-muted-foreground">{seenBy(item)}</span>
    </span>
  )
}

/**
 * The studio's announcements, newest first, on the console's hairline rows.
 * Everyone reads here; administrators also write, edit and archive. A row
 * opens the announcement in full; an address with an id opens it directly —
 * the bell's lines and the Home banner point there.
 */
export function AnnouncementsPage({ id }: { id?: string }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const live = useAnnouncements()
  const all = useAllAnnouncements(isAdmin)
  const list = isAdmin ? all : live
  const [composing, setComposing] = useState<AnnouncementView | 'new' | null>(null)
  const open = id ? list.data?.find((item) => item.id === id) ?? null : null

  return (
    <PageFrame
      title="Announcements"
      description="Updates from the studio."
      actions={isAdmin ? (
        <Button variant="primary" size="sm" icon={<Icons.plus />} onClick={() => setComposing('new')}>
          New Announcement
        </Button>
      ) : undefined}
    >
      {list.isLoading ? (
        <Loading label="Loading announcements" />
      ) : !list.data?.length ? (
        <EmptyNote title="No announcements yet." detail={isAdmin ? 'Published announcements will be listed here.' : 'Announcements from the studio will be listed here.'} />
      ) : (
        <ul>
          {/* The pinned one leads; the rest keep newest first. */}
          {[...list.data].sort((a, b) => Number(b.pinnedAt !== null) - Number(a.pinnedAt !== null)).map((item) => (
            <AnnouncementRow key={item.id} item={item} isAdmin={isAdmin} onEdit={() => setComposing(item)} />
          ))}
        </ul>
      )}

      <AnnouncementReader item={open} onClose={() => navigate({ name: 'announcements' })} />
      <AnnouncementComposer
        open={composing !== null}
        editing={composing === 'new' ? null : composing}
        onClose={() => setComposing(null)}
      />
    </PageFrame>
  )
}

function AnnouncementRow({ item, isAdmin, onEdit }: { item: AnnouncementView; isAdmin: boolean; onEdit: () => void }) {
  const archive = useArchiveAnnouncement()
  const restore = useRestoreAnnouncement()
  const closePoll = useClosePoll()
  const pin = usePinAnnouncement()
  const unpin = useUnpinAnnouncement()
  const pinned = item.pinnedAt !== null
  const unread = item.seenAt === null && item.archivedAt === null
  const read = () => navigate({ name: 'announcements', id: item.id })
  return (
    <li className="border-b border-hairline last:border-0">
      <div
        role="link"
        tabIndex={0}
        onClick={read}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            read()
          }
        }}
        className="-mx-2 grid cursor-pointer grid-cols-[0.5rem_minmax(0,1fr)_auto_2rem] items-center gap-x-3 rounded-lg px-2 py-4 outline-none transition-colors duration-fast hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground sm:gap-x-4"
      >
        <span aria-label={unread ? 'Unread' : undefined} className={unread ? 'size-1.5 rounded-full bg-foreground' : 'size-1.5'} />
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-2">
            {pinned ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-tint px-2 py-0.5 text-micro text-foreground">
                <Icons.pin size="xs" /> Pinned
              </span>
            ) : null}
            <span className={cn('min-w-0 truncate text-ui text-foreground', unread ? 'font-semibold' : 'font-medium')}>{item.title}</span>
          </span>
          <span className="mt-0.5 block truncate text-caption text-muted-foreground">
            {[item.authorName, formatDay(item.createdAt), item.poll ? `Poll · ${item.poll.total} ${item.poll.total === 1 ? 'vote' : 'votes'}` : null, item.archivedAt ? 'Archived' : null].filter(Boolean).join(' · ')}
          </span>
        </span>
        <Viewers item={item} className="hidden sm:flex" />
        {isAdmin ? (
          <div className="justify-self-end" onClick={(event) => event.stopPropagation()}>
            <ActionMenu
              label={`Actions for ${item.title}`}
              sections={[{
                items: [
                  { key: 'edit', label: 'Edit', icon: <Icons.edit size="sm" />, onSelect: onEdit },
                  ...(item.archivedAt !== null ? [] : pinned
                    ? [{ key: 'unpin', label: 'Unpin', icon: <Icons.unpin size="sm" />, disabled: unpin.isPending, onSelect: () => unpin.mutate(item.id) }]
                    : [{ key: 'pin', label: 'Pin To Top', icon: <Icons.pin size="sm" />, disabled: pin.isPending, onSelect: () => pin.mutate(item.id) }]),
                  ...(item.poll && item.poll.closedAt === null && item.archivedAt === null
                    ? [{ key: 'close-poll', label: 'Close Poll', icon: <Icons.poll size="sm" />, disabled: closePoll.isPending, onSelect: () => closePoll.mutate(item.id) }]
                    : []),
                  item.archivedAt === null
                    ? { key: 'archive', label: 'Archive', icon: <Icons.archive size="sm" />, disabled: archive.isPending, onSelect: () => archive.mutate(item.id) }
                    : { key: 'restore', label: 'Restore', icon: <Icons.restore size="sm" />, disabled: restore.isPending, onSelect: () => restore.mutate(item.id) },
                ],
              }]}
            />
          </div>
        ) : <span />}
      </div>
    </li>
  )
}

/**
 * One announcement in full: the title, who and when, the body as written,
 * and at the foot who has seen it. Opening it is reading it — the seen mark
 * is written then, and only then.
 */
export function AnnouncementReader({ item, onClose }: { item: AnnouncementView | null; onClose: () => void }) {
  const seen = useMarkAnnouncementSeen()
  const markedRef = useRef<string | null>(null)
  const [showViewers, setShowViewers] = useState(false)
  useEffect(() => {
    setShowViewers(false)
    if (item && item.seenAt === null && item.archivedAt === null && markedRef.current !== item.id) {
      markedRef.current = item.id
      seen.mutate({ id: item.id })
    }
  }, [item, seen])

  return (
    <Dialog open={item !== null} onClose={onClose} labelledBy="announcement-title" className="sm:max-w-2xl">
      {item ? (
        <article className="flex max-h-[calc(100dvh-4rem)] flex-col rounded-3xl border bg-surface shadow-modal">
          <header className="flex items-start justify-between gap-3 px-6 pt-6 sm:px-8 sm:pt-7">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-micro text-muted-foreground uppercase">
                {item.pinnedAt ? <><Icons.pin size="xs" /> Pinned Announcement</> : 'Announcement'}
              </p>
              <h2 id="announcement-title" className="mt-2 font-display text-[1.6rem] leading-tight text-foreground">{item.title}</h2>
              <p className="mt-1.5 text-caption text-muted-foreground">
                {[item.authorName, formatDay(item.createdAt)].filter(Boolean).join(' · ')}
              </p>
            </div>
            <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom">
              <Icons.close />
            </IconButton>
          </header>
          <div className="min-h-0 overflow-y-auto px-6 pt-5 pb-6 sm:px-8">
            <Markdown source={item.body} className="text-ui leading-relaxed text-foreground" />
            {item.poll ? <PollCard item={item} poll={item.poll} /> : null}
          </div>
          <footer className="border-t border-hairline px-6 py-3 sm:px-8">
            <button
              type="button"
              aria-expanded={showViewers}
              disabled={item.viewers.length === 0}
              onClick={() => setShowViewers((current) => !current)}
              className="-mx-1 flex items-center rounded-md px-1 py-0.5 outline-none transition-colors duration-fast enabled:hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground"
            >
              <Viewers item={item} />
            </button>
            <AnimatePresence initial={false}>
              {showViewers ? (
                <motion.ul key="viewers" {...COLLAPSE} className="max-h-48 overflow-y-auto">
                  {item.viewers.map((viewer) => (
                    <li key={viewer.id} className="flex items-center gap-2.5 py-1.5 first:pt-3">
                      <Avatar name={viewer.name} email={viewer.email} src={viewer.pictureUrl} size="xs" />
                      <span className="min-w-0 flex-1 truncate text-caption text-foreground">{viewer.name ?? viewer.email}</span>
                      <span className="text-caption text-muted-foreground">{formatRelative(viewer.seenAt)}</span>
                    </li>
                  ))}
                </motion.ul>
              ) : null}
            </AnimatePresence>
          </footer>
        </article>
      ) : null}
    </Dialog>
  )
}

/**
 * The poll, under the words: one row per option, the choice made by a
 * press and changed the same way until the poll closes. The counts show
 * once you have voted — or to administrators, who also see the faces of
 * who chose what.
 */
function PollCard({ item, poll }: { item: AnnouncementView; poll: NonNullable<AnnouncementView['poll']> }) {
  const vote = useVote()
  const isAdmin = useSession().data?.account?.role === 'admin'
  const open = poll.closedAt === null && item.archivedAt === null
  const showResults = poll.myVote !== null || !open || isAdmin
  return (
    <section className="mt-6" aria-label="Poll">
      <p className="mb-2 text-micro text-muted-foreground uppercase">
        {['Poll', `${poll.total} ${poll.total === 1 ? 'vote' : 'votes'}`, open ? null : 'Closed'].filter(Boolean).join(' · ')}
      </p>
      <ul className="flex flex-col gap-1.5">
        {poll.options.map((option) => {
          const mine = poll.myVote === option.id
          const share = poll.total > 0 ? Math.round((option.votes / poll.total) * 100) : 0
          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={!open || vote.isPending}
                aria-pressed={mine}
                onClick={() => { if (!mine) vote.mutate({ id: item.id, optionId: option.id }) }}
                className="relative flex w-full items-center gap-3 overflow-hidden rounded-xl bg-tint px-3.5 py-2.5 text-left outline-none transition-colors duration-fast enabled:hover:bg-selected focus-visible:ring-1 focus-visible:ring-foreground disabled:cursor-default"
              >
                {showResults ? (
                  <motion.span
                    aria-hidden
                    className={cn('absolute inset-y-0 left-0', mine ? 'bg-foreground/15' : 'bg-foreground/[0.07]')}
                    initial={false}
                    animate={{ width: `${share}%` }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  />
                ) : null}
                <span
                  aria-hidden
                  className={cn(
                    'relative flex size-4 shrink-0 items-center justify-center rounded-full border',
                    mine ? 'border-foreground bg-foreground text-surface' : 'border-muted-foreground/50',
                  )}
                >
                  {mine ? <Icons.check size="xs" className="text-surface" /> : null}
                </span>
                <span className="relative min-w-0 flex-1 truncate text-ui text-foreground">{option.label}</span>
                {isAdmin && option.voters && option.voters.length > 0 ? (
                  <AvatarStack
                    className="relative"
                    people={option.voters.map((voter) => ({ key: voter.id, name: voter.name, email: voter.email, src: voter.pictureUrl }))}
                    max={3}
                   
                  />
                ) : null}
                {showResults ? (
                  <span className="relative w-10 shrink-0 text-right font-mono text-caption tabular-nums text-muted-foreground">{share}%</span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
      {open && poll.myVote !== null ? <p className="mt-2 text-caption text-muted-foreground">You can change your vote until the poll closes.</p> : null}
    </section>
  )
}

/** The words of an announcement as one plain line, for a banner's excerpt. */
const excerptOf = (body: string): string =>
  body
    .split('\n')
    .filter((line) => line.trim() && !IMAGE_LINE.test(line) && !/^\s*!\[/.test(line))
    .join(' ')
    .replace(/[#*_`>]/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .trim()

/**
 * The top of Home: the pinned announcement, which stays there for everyone
 * whether they have read it or not, and under it the newest unread one,
 * which leaves on its own once it is opened.
 */
export function AnnouncementBanner() {
  const live = useAnnouncements()
  const pinned = live.data?.find((item) => item.pinnedAt !== null) ?? null
  const unread = live.data?.filter((item) => item.seenAt === null && item.id !== pinned?.id) ?? []
  const next = unread[0] ?? null
  return (
    // The pair keeps one gap between them and a wider one before the page.
    <div className="has-[section]:mb-7">
    <AnimatePresence initial={false}>
      {pinned ? (
        <BannerCard
          key={`pin-${pinned.id}`}
          item={pinned}
          label={<><Icons.pin size="xs" /> Pinned{pinned.seenAt === null ? ' · New' : ''}{pinned.poll ? ' · Poll' : ''}</>}
        />
      ) : null}
      {next ? (
        <BannerCard
          key={next.id}
          item={next}
          label={<><span aria-hidden className="size-1.5 rounded-full bg-foreground" /> New Announcement{next.poll ? ' · Poll' : ''}{unread.length > 1 ? ` · ${unread.length - 1} more` : ''}</>}
        />
      ) : null}
    </AnimatePresence>
    </div>
  )
}

function BannerCard({ item, label }: { item: AnnouncementView; label: React.ReactNode }) {
  const excerpt = excerptOf(item.body)
  return (
    <motion.section {...COLLAPSE} className="overflow-hidden" aria-label={item.pinnedAt ? 'Pinned announcement' : 'New announcement'}>
      <button
        type="button"
        onClick={() => navigate({ name: 'announcements', id: item.id })}
        className="group mb-3 block w-full rounded-2xl bg-tint px-6 py-5 text-left outline-none transition-colors duration-fast hover:bg-selected focus-visible:ring-1 focus-visible:ring-foreground"
      >
        <span className="flex items-center gap-2 text-micro text-muted-foreground uppercase">{label}</span>
        <span className="mt-2 block font-display text-[1.6rem] leading-tight text-foreground">{item.title}</span>
        {excerpt ? <span className="mt-1.5 line-clamp-2 block text-ui text-muted-foreground">{excerpt}</span> : null}
        <span className="mt-3 flex items-center gap-1 text-caption leading-none text-foreground">
          {item.poll ? 'Read and vote' : 'Read announcement'}
          <span className="inline-flex items-center transition-transform duration-fast group-hover:translate-x-0.5" aria-hidden><Icons.disclosure size="sm" /></span>
        </span>
      </button>
    </motion.section>
  )
}

// ---- The composer ------------------------------------------------------------

/**
 * Writing an announcement on a page, not in a form: the title in large type
 * above the shared writing surface, and the poll's options opening above its
 * toolbar. Ctrl+Enter publishes.
 */
function AnnouncementComposer({ open, editing, onClose }: { open: boolean; editing: AnnouncementView | null; onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [options, setOptions] = useState<string[] | null>(null)
  const area = useRef<HTMLTextAreaElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const create = useCreateAnnouncement(onClose)
  const update = useUpdateAnnouncement(onClose)

  useEffect(() => {
    if (!open) return
    setTitle(editing?.title ?? '')
    setBody(editing?.body ?? '')
    setOptions(null)
    // The dialog takes focus as it rises; the title takes it back after.
    const timer = setTimeout(() => titleRef.current?.focus(), 60)
    return () => clearTimeout(timer)
  }, [open, editing])

  const busy = create.isPending || update.isPending
  const filled = (options ?? []).map((option) => option.trim()).filter(Boolean)
  const pollReady = options === null || (filled.length >= 2 && new Set(filled.map((option) => option.toLowerCase())).size === filled.length)
  const ready = title.trim().length > 0 && body.trim().length > 0 && pollReady
  const publish = () => {
    if (!ready || busy) return
    if (editing) update.mutate({ id: editing.id, input: { title: title.trim(), body: body.trim() } })
    else create.mutate({ title: title.trim(), body: body.trim(), ...(options !== null ? { poll: { options: filled } } : {}) })
  }

  return (
    <Dialog open={open} onClose={onClose} labelledBy="compose-announcement-label" className="sm:max-w-2xl">
      <form
        className="flex max-h-[calc(100dvh-4rem)] flex-col rounded-3xl border bg-surface shadow-modal"
        onSubmit={(event) => {
          event.preventDefault()
          publish()
        }}
      >
        <div className="flex items-center justify-between px-6 pt-5 sm:px-8">
          <p id="compose-announcement-label" className="text-micro text-muted-foreground uppercase">
            {editing ? 'Edit Announcement' : 'New Announcement'}
          </p>
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom">
            <Icons.close />
          </IconButton>
        </div>

        <WritingSurface
          value={body}
          onChange={setBody}
          onSubmit={publish}
          label="Announcement"
          placeholder="Write something for the studio…"
          maxLength={20_000}
          areaRef={area}
          header={
            <input
              ref={titleRef}
              aria-label="Title"
              placeholder="Title"
              value={title}
              maxLength={140}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  area.current?.focus()
                }
              }}
              className="w-full bg-transparent font-display text-[1.75rem] leading-tight text-foreground outline-none placeholder:text-muted-foreground/50"
            />
          }
          panels={
            <>
              {options !== null ? (
                <motion.div key="poll" {...COLLAPSE} className="overflow-hidden">
                  <div className="border-t border-hairline px-6 py-4 sm:px-8">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-micro text-muted-foreground uppercase">Poll</p>
                      <button type="button" onClick={() => setOptions(null)} className="text-caption text-muted-foreground transition-colors duration-fast hover:text-foreground">
                        Remove poll
                      </button>
                    </div>
                    <ul className="flex flex-col gap-1">
                      {options.map((option, index) => (
                        <li key={index} className="flex items-center gap-3">
                          <span aria-hidden className="size-4 shrink-0 rounded-full border border-muted-foreground/50" />
                          <input
                            aria-label={`Option ${index + 1}`}
                            placeholder={`Option ${index + 1}`}
                            value={option}
                            maxLength={80}
                            autoFocus={index === options.length - 1 && index > 1}
                            onChange={(event) => setOptions(options.map((entry, at) => (at === index ? event.target.value : entry)))}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault()
                                if (index === options.length - 1 && options.length < 6) setOptions([...options, ''])
                              }
                            }}
                            className="min-w-0 flex-1 bg-transparent py-1.5 text-ui text-foreground outline-none placeholder:text-muted-foreground/50"
                          />
                          {options.length > 2 ? (
                            <IconButton label={`Remove option ${index + 1}`} size="sm" onClick={() => setOptions(options.filter((_, at) => at !== index))}>
                              <Icons.close />
                            </IconButton>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    {options.length < 6 ? (
                      <button type="button" onClick={() => setOptions([...options, ''])} className="mt-1 flex items-center gap-3 py-1.5 text-ui text-muted-foreground transition-colors duration-fast hover:text-foreground">
                        <span className="flex size-4 items-center justify-center" aria-hidden><Icons.plus size="sm" /></span>
                        Add option
                      </button>
                    ) : null}
                  </div>
                </motion.div>
              ) : null}
              {editing?.poll ? (
                <p key="poll-fixed" className="border-t border-hairline px-6 py-3 text-caption text-muted-foreground sm:px-8">
                  This announcement has a poll. Its options cannot change after publishing.
                </p>
              ) : null}
            </>
          }
          tools={editing ? null : (
            <Tool label="Poll" pressed={options !== null} onClick={() => setOptions(options === null ? ['', ''] : null)}><Icons.poll /></Tool>
          )}
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary" size="sm" busy={busy} disabled={!ready}>
                {editing ? 'Save' : 'Publish'}
              </Button>
            </>
          }
        />
      </form>
    </Dialog>
  )
}
